import { action, computed, makeObservable, observable } from 'mobx';
import { MarketplaceFeaturesParams } from '../constants/marketplace';
import {
  IMarketplaceFilters,
  MarketplaceFeatureKey,
  MarketplaceFeatureType,
  TMarketplaceFeature,
  TMarketplaceUpdateParams,
  marketplaceFeatureCast,
} from '../types/marketplace';
import itemsStore from '../../MenuManagementScreen/screens/ItemsScreen/store/itemsStore';
import { DEBOUNCE_TIME } from '~/constants/main';
import { isCanceledRequest } from '~/helpers';
import { localeStore } from '~/mobx/localesStore';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';
import { booleanToNumber } from '~/utils/helpers';
import { debounce } from '~/utils/perfomance';
import { appStore } from '~/mobx/appStore';

class MarketplaceStore {
  constructor() {
    makeObservable(this);
  }

  private _fetchFeatureAbortController = new AbortController();

  @observable selectedFeature: TMarketplaceFeature | null = null;
  @observable features: TMarketplaceFeature[] = [];

  @observable filters: IMarketplaceFilters = { type: MarketplaceFeatureType.All, showPurchased: false, search: '' };
  @observable loadingStatus = LoadingStatus.Init;

  @observable isAnyUnsavedData = false;

  @action changeFilters = (filters: Partial<IMarketplaceFilters>) => {
    this.filters = { ...this.filters, ...filters };
  };

  @action setUnsavedData = (value: boolean) => {
    this.isAnyUnsavedData = value;
  };

  changeSelectedFeatureData = (data: Partial<TMarketplaceFeature>) => {
    if (!this.selectedFeature) return;
    this.setSelectedFeature({ ...this.selectedFeature, ...data });
    this._debouncedUpdate({
      donation_branch_id: data.donationBranchId,
      franchise_unintegrated_branches: data.franchise_unintegrated_branches,
      [this.selectedFeature.key]: data.status !== undefined ? booleanToNumber(data.status) : undefined,
    });
  };

  @action fetchMarketplaceFeature = async (featureKey: MarketplaceFeatureKey) => {
    try {
      this._fetchFeatureAbortController.abort();
      this._fetchFeatureAbortController = new AbortController();

      this.loadingStatus = LoadingStatus.Loading;

      const feature = await client.get(`v3/marketplace/${featureKey}`, { signal: this._fetchFeatureAbortController.signal });
      this.setSelectedFeature(marketplaceFeatureCast(feature));

      this.loadingStatus = LoadingStatus.Success;
    } catch (error) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'MarketplaceStore fetchMarketplaceFeature' });
    }
  };

  @action fetchMarketplaceFeaturesList = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const features = await client.get('v3/marketplace');

      this.features = features
        .map(marketplaceFeatureCast)
        .filter((feature: any) => feature.type.some((type: any) => Object.values(MarketplaceFeatureType).includes(type)));

      this.loadingStatus = LoadingStatus.Success;
    } catch (error) {
      if (isCanceledRequest(error)) {
        this.loadingStatus = LoadingStatus.Success;
      } else {
        this.loadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'MarketplaceStore fetchMarketplaceFeaturesList' });
      }
    }
  };

  @action updateMarketplaceFeature = async (params: TMarketplaceUpdateParams) => {
    try {
      await client.post('v3/marketplace', { ...params });
      await appStore.fetchSettings();
      await itemsStore.fetchItemsCount();
    } catch (error) {
      logger.error({ error, place: 'MarketplaceStore fetchMarketplaceFeatures' });
    }
  };

  private _debouncedUpdate = debounce(this.updateMarketplaceFeature, DEBOUNCE_TIME.MEDIUM);

  @action setSelectedFeature = (feature: TMarketplaceFeature | null) => {
    this.selectedFeature = feature;
  };

  @computed get filteredFeaturesList(): TMarketplaceFeature[] {
    return this.features.filter(
      (feature) => this._filterBySearch(feature) && this._filterByFeatureType(feature) && this._filterByPurchasedStatus(feature)
    );
  }

  @action public getFeatures(key: MarketplaceFeatureKey): TMarketplaceFeature | undefined {
    return this.features.find((feature) => feature.key === key);
  }

  private _filterBySearch = (feature: TMarketplaceFeature) => {
    const name = localeStore.t(MarketplaceFeaturesParams[feature.key]?.name ?? '').toLowerCase();
    const description = localeStore.t(`${name}_description`).toLowerCase();
    const searchText = this.filters.search.toLowerCase();

    return name.includes(searchText) || description.includes(searchText);
  };

  private _filterByFeatureType = (feature: TMarketplaceFeature) => {
    return this.filters.type !== MarketplaceFeatureType.All ? feature.type.includes(this.filters.type) : true;
  };

  private _filterByPurchasedStatus = (feature: TMarketplaceFeature) => {
    return this.filters.showPurchased ? feature.isPurchased : true;
  };
}

const marketplaceStore = new MarketplaceStore();

export default marketplaceStore;
